import { useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { Link } from 'react-router-dom';
import { themeCatalog, themeSwatches, useTheme } from '../theme';
import { DecorativeIcon } from '../organization/DecorativeIcon';

export function Wordmark({ href = '/' }: { href?: string }) {
  const { theme } = useTheme();
  const source = theme === 'light' ? '/nimbalyst-logo.svg' : '/nimbalyst-logo-white.svg';
  return (
    <Link className="wordmark inline-flex items-center" to={href} aria-label="Nimbalyst console home">
      <img className="block h-[1.85rem] w-auto" src={source} alt="Nimbalyst" width={116} height={32} />
    </Link>
  );
}

/**
 * The console's form-control shape, for `<input>` and `<select>` alike.
 *
 * Every property here is one Tailwind's Preflight resets and the browser would
 * otherwise have supplied: `border-width: 0` is global under Preflight, and
 * padding is zeroed on form elements. The native `<select>` arrow survives --
 * `appearance` is not reset -- so it is deliberately not redrawn.
 *
 * Radius, padding and background are left to the caller; the rest is fixed so
 * two controls on the same page cannot disagree about what focus looks like.
 */
export const FIELD_CONTROL = 'border border-nim text-nim outline-none transition-colors duration-150 placeholder:text-nim-muted placeholder:opacity-70 focus:border-nim-focus focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--nim-border-focus)_35%,transparent)]';

/** `FIELD_CONTROL` plus the defaults most of the admin forms want. */
export const FIELD_BOX = `${FIELD_CONTROL} rounded-lg bg-nim`;

/**
 * The theme picker in the profile menu.
 *
 * A popover of the desktop's built-in catalog, drawn as the desktop's Themes
 * panel draws it -- icon, name, blurb, a check on the active one -- plus a
 * three-colour swatch strip, which is the one thing a `<select>` of six names
 * could never give: with Monokai and two Solarized variants on the list, the
 * names stop being enough to choose by.
 *
 * Positioned with `@floating-ui/react` and portalled, because it opens inside
 * an already-portalled menu that clips its overflow.
 */
export function ThemePicker() {
  const { theme, setTheme } = useTheme();
  const catalog = themeCatalog();
  const active = catalog.find((entry) => entry.id === theme) ?? catalog[0]!;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const interactions = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, { role: 'menu' }),
    useListNavigation(context, {
      listRef: itemRefs,
      activeIndex,
      onNavigate: setActiveIndex,
      focusItemOnOpen: true,
      loop: true,
    }),
  ]);

  return (
    <>
      <button
        ref={refs.setReference}
        {...interactions.getReferenceProps()}
        type="button"
        className={`theme-picker-trigger flex h-[1.8rem] min-w-[7.5rem] cursor-pointer items-center gap-1.5 rounded-[5px] bg-nim-secondary px-[0.55rem] text-[0.72rem] text-nim-muted hover:border-nim-focus hover:text-nim ${FIELD_CONTROL}`}
        aria-label={`Theme: ${active.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <ThemeSwatchStrip theme={active.id} />
        <span className="min-w-0 flex-1 truncate text-left">{active.name}</span>
        <DecorativeIcon icon="unfold_more" size={14} />
      </button>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...interactions.getFloatingProps()}
              className="theme-picker-menu z-[110] w-[16rem] rounded-[9px] border border-nim bg-nim p-1.5 text-nim shadow-[var(--console-shadow-lg)]"
              aria-label="Theme"
            >
              {catalog.map((entry, index) => {
                const selected = entry.id === theme;
                return (
                  <button
                    key={entry.id}
                    ref={(node) => { itemRefs.current[index] = node; }}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    tabIndex={activeIndex === index ? 0 : -1}
                    data-theme-option={entry.id}
                    className={`theme-picker-option flex w-full cursor-pointer items-center gap-2.5 rounded-[5px] border-none px-2 py-[7px] text-left ${
                      selected ? 'bg-nim-selected text-nim' : 'bg-transparent text-nim-muted hover:bg-nim-hover hover:text-nim'
                    }`}
                    {...interactions.getItemProps({
                      onClick: () => {
                        setTheme(entry.id);
                        setOpen(false);
                      },
                    })}
                  >
                    <span className="grid size-[26px] shrink-0 place-items-center rounded-md bg-nim-tertiary">
                      <DecorativeIcon icon={entry.icon} size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <strong className="block truncate text-[0.78rem] font-semibold text-nim">{entry.name}</strong>
                      <span className="block truncate text-[0.66rem] text-nim-faint">{entry.description}</span>
                    </span>
                    <ThemeSwatchStrip theme={entry.id} />
                    {selected && <DecorativeIcon icon="check" size={16} className="text-nim-primary" />}
                  </button>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

/** Surface, secondary surface and accent, so six names become six pictures. */
function ThemeSwatchStrip({ theme }: { theme: Parameters<typeof themeSwatches>[0] }) {
  return (
    <span className="theme-swatch-strip flex shrink-0 items-center gap-px overflow-hidden rounded-[3px] border border-nim" aria-hidden="true">
      {themeSwatches(theme).map((color, index) => (
        <span key={index} className="block size-[9px]" style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

/**
 * A grid rather than a block: every caller passes a headline and a sentence as
 * two inline children, and inline children run together on one line.
 */
const PANEL_BASE = 'grid gap-[0.4rem] rounded-[0.9rem] border px-8 py-12 text-center';
const PANEL_TONE = {
  loading: 'border-nim bg-nim-secondary text-nim-muted',
  error: 'border-[var(--nim-error)] text-[var(--nim-error)] bg-[color-mix(in_srgb,var(--nim-error)_12%,transparent)]',
  empty: 'border-nim bg-nim-secondary',
} as const;

export function PanelState({
  children,
  tone = 'loading',
  className,
  ...rest
}: {
  children: ReactNode;
  tone?: keyof typeof PANEL_TONE;
  className?: string;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={`${tone}-panel ${PANEL_BASE} ${PANEL_TONE[tone]} ${className ?? ''}`}>
      {children}
    </div>
  );
}

/**
 * The pre-connect frame for a room-backed surface.
 *
 * Flat, not a shimmer: these resolve in well under a second on a normal
 * connection, and placeholder rows that never become real rows read as a stall.
 */
export function CollabLoadingPanel({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="browser-collab-loading grid h-full place-content-center justify-items-center gap-[0.4rem] p-8 text-center" role="status">
      <span
        className="browser-collab-loading-spinner mb-[0.35rem] size-[1.15rem] animate-spin rounded-full border-2 border-nim border-t-[color:var(--nim-primary)]"
        aria-hidden="true"
      />
      <strong className="text-[0.95rem] font-semibold">{title}</strong>
      <span className="text-[0.85rem] text-nim-muted">{detail}</span>
    </div>
  );
}
